import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../api/superAdminAPI';

export const useSuperAdmin = (filters = {}) => {
  const hasFilters = Object.keys(filters).length > 0;
  
  const systemOverview = useQuery({
    queryKey: ['superAdmin', 'overview'],
    queryFn: () => superAdminAPI.getSystemOverview(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const allUsers = useQuery({
    queryKey: ['superAdmin', 'users'],
    queryFn: () => superAdminAPI.getAllUsersData(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // Always fetch users for dropdown population
  });

  const allDocuments = useQuery({
    queryKey: ['superAdmin', 'documents', filters],
    queryFn: () => superAdminAPI.getAllDocumentsData(filters),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: hasFilters, // Only fetch when filters are provided
  });

  const allTaskAssignments = useQuery({
    queryKey: ['superAdmin', 'taskAssignments', filters],
    queryFn: () => superAdminAPI.getAllTaskAssignmentsData(filters),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: hasFilters, // Only fetch when filters are provided
  });

  const allAuditLogs = useQuery({
    queryKey: ['superAdmin', 'auditLogs', filters],
    queryFn: () => superAdminAPI.getAllAuditLogsData(filters),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: hasFilters, // Only fetch when filters are provided
  });

  const systemHealth = useQuery({
    queryKey: ['superAdmin', 'health'],
    queryFn: () => superAdminAPI.getSystemHealth(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });


  const isLoading = systemOverview.isLoading || 
                   allUsers.isLoading || 
                   allDocuments.isLoading || 
                   allTaskAssignments.isLoading ||
                   allAuditLogs.isLoading || 
                   systemHealth.isLoading;

  const error = systemOverview.error || 
               allUsers.error || 
               allDocuments.error || 
               allTaskAssignments.error ||
               allAuditLogs.error || 
               systemHealth.error;

  const refetch = () => {
    systemOverview.refetch();
    allUsers.refetch();
    allDocuments.refetch();
    allTaskAssignments.refetch();
    allAuditLogs.refetch();
    systemHealth.refetch();
  };

  return {
    systemOverview: systemOverview.data,
    allUsers: allUsers.data,
    allDocuments: allDocuments.data,
    allTaskAssignments: allTaskAssignments.data,
    allAuditLogs: allAuditLogs.data,
    systemHealth: systemHealth.data,
    isLoading,
    error,
    refetch
  };
};

export const useSuperAdminStatus = () => {
  return useQuery({
    queryKey: ['superAdmin', 'status'],
    queryFn: () => superAdminAPI.checkSuperAdminStatus(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

export const useUserActivityDetails = (userId) => {
  return useQuery({
    queryKey: ['superAdmin', 'userActivity', userId],
    queryFn: () => superAdminAPI.getUserActivityDetails(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSystemStatistics = () => {
  return useQuery({
    queryKey: ['superAdmin', 'statistics'],
    queryFn: () => superAdminAPI.getSystemStatistics(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

export const useEntityAuditLogs = (entityType, entityId, filters = {}) => {
  return useQuery({
    queryKey: ['superAdmin', 'entityAuditLogs', entityType, entityId, filters],
    queryFn: () => superAdminAPI.getEntityAuditLogs(entityType, entityId, filters),
    enabled: !!entityType && !!entityId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });
};

